/**
 * dashboardLists.ts
 *
 * Data layer for the per-role, per-administration-unit dashboard. Every count
 * here is meant to equal what the same user sees in the matching list table.
 * For each sidebar list page this file uses the SAME role -> service function
 * choice and the SAME client-side tab predicates as the page, calling the same
 * exported functions from src/services/*. Each mirrored chain has a comment
 * citing the page file:line it copies.
 *
 * How this differs from the requested interface (kept to a minimum):
 *  - MODULES entries and ModuleCount have an optional `state`. The Conduct
 *    Legal Research sidebar sub-items all link to "/conduct-legal-research" and
 *    pick the tab with router state `{ activeTab: "<English tab label>" }`
 *    (Sidebar.tsx:169-190). The route alone can't tell them apart, so the UI
 *    has to pass `state` to navigate()/<Link>.
 *  - MODULES entries have an optional `parentLabelKey` for sidebar items that
 *    sit inside a dropdown ("ConductLegalResearch" and "LegalTranslation"),
 *    because their own labelKey ("External", "Internal", ...) means little
 *    on its own.
 *  - `label` is set when the sidebar text is not a real i18n key: t() then
 *    falls back to the key itself. CheckingAccuracyOfSectors and
 *    RegisteringDirectives are not in any locale file, so the sidebar shows
 *    the raw key. The Conduct Legal Research sub-items are passed to t() as
 *    English text ("External Request"), which is also not a key.
 *
 * Things every page does that this file does NOT copy:
 *  - The requested_by_user lookup (getStakeholder / getEmployeesByAdminAndOrg)
 *    is used only for search and display, never for filtering, so it is
 *    skipped. Exception: on several pages (legislativeDrafting.tsx:250-257,
 *    legalAdvisory.tsx:262-266, sectorDirectives, registerDirectives,
 *    legalTranslation, legalTraningProvision, the two *Zone pages)
 *    getStakeholder is awaited OUTSIDE a try block inside Promise.all. If that
 *    call rejects (a network error or a non-JSON body), filteredData is never
 *    set and the page shows an empty table. That case is not copied here; we
 *    count the rows.
 *  - DataTable (components/DataTable.tsx:57-62) starts unsorted, so the page
 *    order is the API order. `recent` is the first 6 rows in API order.
 *  - DataTable treats a non-array `data` as [] (DataTable.tsx:62). The same
 *    guard is applied here.
 *
 * Token: pages use `contextData.token || user.access_token`. Only `user` is
 * passed in, so we use user.access_token || user.access || the sessionStorage
 * "authToken". App.tsx sets all three to the same JWT (App.tsx:160-186,
 * 300-360).
 */

import i18n from "../../i18n";
import { getTokenPayloadSafely } from "App";
import {
  LAW_IMPLEMENTATION_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_TRAINING_PROVISION_REQUEST_STATUS,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  LEGAL_RESEARCH_COORDINATOR_ROLE_CONDUCT_RESEARCH_TABS,
  LEGAL_RESEARCH_PROSECUTOR_ROLE_CONDUCT_RESEARCH_TABS,
  OAGB_MANAGEMENT_HEAD_ROLE_CONDUCT_RESEARCH_TABS,
  REGISTRAL_ROLE_CONDUCT_RESEARCH_TABS,
  STAKEHOLDER_ROLE_CONDUCT_RESEARCH_TABS,
} from "utils/constantValues";
import {
  getCommitteeLegalResearchRequests,
  getCommitteeLegalResearchRequestsByStakeholder,
  getInternalLegalResearchRequests,
  getInternalLegalResearchRequestsByProsecutor,
  getLegalResearchRequests,
  getLegalResearchRequestsByStakeHolder,
} from "services/legalResearchService";
import {
  getLegislativeDraftings,
  getLegislativeDraftingsByStakeHolderID,
} from "services/legislativeService";
import {
  getLegalAdvisories,
  getLegalAdvisoriesByProsecutor,
  getLegalAdvisoriesByStakeholder,
} from "services/legalAdvisoryService";
import {
  getSectorDirectives,
  getSectorDirectivesByStakeholder,
} from "services/sectorDirectives";
import {
  getRegisterDirectives,
  getRegisterDirectivesByProsecutor,
  getRegisterDirectivesByStakeholder,
} from "services/registerDirectivesService";
import {
  getLegalTranslationsByProsecutorID,
  getLegalTranslationsByStakeHolderID,
  getLegalTranslationsExternal,
  getLegalTranslationsInternal,
} from "services/legalTranslationService";
import {
  getAuditLawImplementations,
  getLawImplementationsByRegistryAndUser,
  getLawImplementationsByRegistryNumber,
} from "services/lawImplementationService";
import {
  getAllDistributedTowerData,
  getLegalAwareness,
  getLegalAwarenessByProsecutor,
  getLegalAwarenessZoneByAdminUnit,
} from "services/legalAwarenessService";
import {
  getLegalTrainingProvisions,
  getLegalTrainingProvisionsByProsecutor,
  getLegalTrainingProvisionsByStakeHolder,
  getLegalTrainingProvisionsZoneByAdminUnit,
} from "services/legalTrainingProvision";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ModuleKey = string;

export interface ModuleDef {
  key: ModuleKey;
  route: string;
  /** i18n key the sidebar passes to t() for this item. */
  labelKey: string;
  /** Literal text, set when labelKey is not a real locale key (see header). */
  label?: string;
  /** i18n key of the sidebar dropdown this item sits in, if any. */
  parentLabelKey?: string;
  /** Router state the sidebar link passes (Conduct Legal Research sub-items). */
  state?: { activeTab: string };
}

export interface StatusCount {
  key: string;
  label: string;
  labelKey?: string;
  count: number;
}

export interface RecentRow {
  id: string;
  title: string;
  subtitle?: string;
  status?: string | null;
  date?: string | null;
  route: string;
}

export interface ModuleCount {
  key: ModuleKey;
  route: string;
  state?: { activeTab: string };
  total: number;
  byStatus: StatusCount[];
  recent: RecentRow[];
}

// ---------------------------------------------------------------------------
// Module registry
// ---------------------------------------------------------------------------

// Conduct Legal Research sidebar sub-items. The sidebar renders each tab
// string from utils/constantValues *_CONDUCT_RESEARCH_TABS through
// subNavItem(), which links to "/conduct-legal-research" with
// state { activeTab: tab } (Sidebar.tsx:169-190).
const CLR_TAB_KEYS: Record<string, ModuleKey> = {
  "External Request": "clr.external",
  Archived: "clr.archived",
  "Legal Research Request": "clr.legalResearch",
  "Committee Request": "clr.committee",
  "Internal Request": "clr.internal",
  "Proposal Submission": "clr.proposalSubmission",
};

const clrModule = (tab: string): ModuleDef => ({
  key: CLR_TAB_KEYS[tab],
  route: "/conduct-legal-research",
  labelKey: tab,
  label: tab,
  parentLabelKey: "ConductLegalResearch",
  state: { activeTab: tab },
});

export const MODULES: ModuleDef[] = [
  ...Object.keys(CLR_TAB_KEYS).map(clrModule),
  { key: "legislativeDrafting", route: "/legislative-drafting", labelKey: "LegislativeDrafting" },
  { key: "legalAdvisory", route: "/legal-advisory", labelKey: "LegalAdvisory" },
  {
    key: "sectorDirectives",
    route: "/sector-directives",
    labelKey: "CheckingAccuracyOfSectors",
    label: "CheckingAccuracyOfSectors", // not in any locale; sidebar shows the raw key
  },
  {
    key: "registerDirectives",
    route: "/registering-directives",
    labelKey: "RegisteringDirectives",
    label: "RegisteringDirectives", // not in any locale; sidebar shows the raw key
  },
  {
    key: "legalTranslationExternal",
    route: "/legal-translation/external",
    labelKey: "External",
    parentLabelKey: "LegalTranslation",
  },
  {
    key: "legalTranslationInternal",
    route: "/legal-translation/internal",
    labelKey: "Internal",
    parentLabelKey: "LegalTranslation",
  },
  // External-user sidebar only (Sidebar.tsx:988-992): a plain link, no dropdown.
  { key: "legalTranslation", route: "/legal-translation", labelKey: "LegalTranslation" },
  { key: "lawImplementation", route: "/law-implementation", labelKey: "LawImplementation" },
  { key: "legalAwarenessCreation", route: "/legal-awareness-creation", labelKey: "LegalAwarenessCreation" },
  { key: "legalAwarenessCreationZone", route: "/legal-awareness-creation-zone", labelKey: "LegalAwarenessCreation" },
  { key: "legalTrainingProvision", route: "/legal-training-provision", labelKey: "LegalTrainingProvision" },
  { key: "legalTrainingProvisionZone", route: "/legal-training-provision-zone", labelKey: "LegalTrainingProvision" },
];

const MODULE_BY_KEY: Record<string, ModuleDef> = MODULES.reduce(
  (acc, m) => ({ ...acc, [m.key]: m }),
  {} as Record<string, ModuleDef>
);

// ---------------------------------------------------------------------------
// visibleModules: mirrors components/Sidebar.tsx
// ---------------------------------------------------------------------------

// Mirrors Sidebar.tsx:103-129 getConductLegalResearchTabs(). Quirks copied:
// "registrar", "OAGB Management Head" and "Legal Research Prosecutor" are
// exact-match lookups in roles (lowercase / with spaces), so they almost never
// match real role names. A user with the "Registrar" role is not in the
// coordinator list either, so they get the STAKEHOLDER tabs.
function getConductLegalResearchTabs(user: any): string[] {
  if (!user?.roles) {
    return STAKEHOLDER_ROLE_CONDUCT_RESEARCH_TABS;
  }
  if (user.roles.includes("registrar")) {
    return REGISTRAL_ROLE_CONDUCT_RESEARCH_TABS;
  } else if (user.roles.includes("OAGB Management Head")) {
    return OAGB_MANAGEMENT_HEAD_ROLE_CONDUCT_RESEARCH_TABS;
  } else if (user.roles.includes("Legal Research Prosecutor")) {
    return LEGAL_RESEARCH_PROSECUTOR_ROLE_CONDUCT_RESEARCH_TABS;
  } else if (
    [
      "LegalResearchCoordinator",
      "attorneygeneral",
      "LRDIFUDeputy",
      "OAGBManagementHead",
      "LRDIFUDDirector",
      "LegalResearchProsecutor",
      "GeneralAttorney",
    ].some((r) => user.roles.includes(r))
  ) {
    return LEGAL_RESEARCH_COORDINATOR_ROLE_CONDUCT_RESEARCH_TABS;
  }
  return STAKEHOLDER_ROLE_CONDUCT_RESEARCH_TABS;
}

const clrKeys = (user: any): ModuleKey[] =>
  getConductLegalResearchTabs(user)
    .map((tab) => CLR_TAB_KEYS[tab])
    .filter(Boolean);

/**
 * Returns exactly the list pages the sidebar shows this user, in sidebar
 * order. Dashboard ("/") and Reports ("/reports") are left out. They are not
 * list pages, and "/reports" has no route in App.tsx.
 */
export function visibleModules(user: any): ModuleKey[] {
  // Flags: Sidebar.tsx:255-266. Branch order below is the order of the early
  // returns in Sidebar.tsx and must not be reordered.
  const roles = user?.roles;
  const has = (r: string) => !!roles?.includes(r);
  // App.tsx:188-190 and 357-359: <Sidebar isInternalUser> is true only when
  // user_type === "internal".
  const isInternalUser = user?.user_type === "internal";

  // Sidebar.tsx:270-294
  if (has("ImpAwarenessCordinator")) {
    return ["legalAwarenessCreation", "lawImplementation"];
  }
  // Sidebar.tsx:298-424 (same items as the full internal layout)
  if (has("LRDIFUDDirector")) {
    return fullInternal(user);
  }
  // Sidebar.tsx:427-498
  if (has("LegalTranslationCordinator")) {
    return ["legalTranslationExternal", "legalTranslationInternal"];
  }
  // Sidebar.tsx:501-553
  if (has("LegalResearchCoordinator")) {
    return [
      ...clrKeys(user),
      "legislativeDrafting",
      "legalAdvisory",
      "sectorDirectives",
      "registerDirectives",
      "legalTrainingProvision",
    ];
  }
  // Sidebar.tsx:556-580
  if (has("ImpAwarenessProsecutor")) {
    return ["legalAwarenessCreation", "lawImplementation"];
  }
  // Sidebar.tsx:583-654
  if (has("LegalTranslationProsecutor")) {
    return ["legalTranslationExternal", "legalTranslationInternal"];
  }
  // Sidebar.tsx:657-705 (note the different item order)
  if (has("LegalResearchProsecutor")) {
    return [
      ...clrKeys(user),
      "legislativeDrafting",
      "legalTrainingProvision",
      "sectorDirectives",
      "registerDirectives",
      "legalAdvisory",
    ];
  }
  // Sidebar.tsx:707-738
  if (has("WoredaLDR")) {
    return ["legalAwarenessCreation"];
  }
  // Sidebar.tsx:741-799 (the else branch at 780-793 is unreachable)
  if (has("ZonalLRD") || has("ZonalLIF")) {
    return ["legalAwarenessCreationZone", "lawImplementation", "legalTrainingProvisionZone"];
  }
  // Sidebar.tsx:801-834
  if (has("ZonalOfficeOfCouncilHead")) {
    return ["legalTrainingProvisionZone", "legalAwarenessCreationZone"];
  }
  // Sidebar.tsx:835-961
  if (isInternalUser) {
    return fullInternal(user);
  }
  // Sidebar.tsx:962-1002 (external / stakeholder layout)
  return [
    ...clrKeys(user),
    "legislativeDrafting",
    "legalAdvisory",
    "sectorDirectives",
    "registerDirectives",
    "legalTranslation",
    "legalTrainingProvision",
  ];
}

function fullInternal(user: any): ModuleKey[] {
  return [
    ...clrKeys(user),
    "legislativeDrafting",
    "legalAdvisory",
    "sectorDirectives",
    "registerDirectives",
    "legalTranslationExternal",
    "legalTranslationInternal",
    "lawImplementation",
    "legalAwarenessCreation",
    "legalTrainingProvision",
  ];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const RECENT_LIMIT = 6;

const tr = (key: string): string => i18n.t(key) as string;
// Pages build tab labels as `t("Key") || "Fallback"`. t() returns the key
// when it is missing, so the fallback is never used. We compare by key.
const enLabel = (key: string, fallback: string) => fallback || key;

function getToken(user: any): string {
  return (
    user?.access_token ||
    user?.access ||
    (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("authToken") : "") ||
    ""
  );
}

const asArray = (v: any): any[] => (Array.isArray(v) ? v : []);

const str = (v: any): string | undefined =>
  v === undefined || v === null || v === "" ? undefined : String(v);

function toError(e: unknown, moduleKey: string): Error {
  if (e instanceof Error) return e;
  return new Error(`Failed to load ${moduleKey}: ${String(e)}`);
}

interface TabDef {
  key: string;
  labelKey: string;
  label: string;
}

function countTabs(rows: any[], tabs: TabDef[], match: (row: any, tabKey: string) => boolean): StatusCount[] {
  return tabs.map((tab) => ({
    key: tab.key,
    labelKey: tab.labelKey,
    label: tab.label,
    count: rows.filter((r) => match(r, tab.key)).length,
  }));
}

const tab = (key: string, fallback: string, labelKey = key): TabDef => ({
  key,
  labelKey,
  label: enLabel(key, fallback),
});

// ---------------------------------------------------------------------------
// loadModuleCount
// ---------------------------------------------------------------------------

export async function loadModuleCount(key: ModuleKey, user: any): Promise<ModuleCount> {
  const def = MODULE_BY_KEY[key];
  if (!def) throw new Error(`Unknown dashboard module: ${key}`);
  try {
    switch (key) {
      case "legislativeDrafting":
        return await loadLegislativeDrafting(def, user);
      case "legalAdvisory":
        return await loadAdvisoryLike(def, user, "advisory");
      case "sectorDirectives":
        return await loadAdvisoryLike(def, user, "sector");
      case "registerDirectives":
        return await loadAdvisoryLike(def, user, "register");
      case "legalTranslationExternal":
      case "legalTranslation":
        return await loadLegalTranslation(def, user, false);
      case "legalTranslationInternal":
        return await loadLegalTranslation(def, user, true);
      case "lawImplementation":
        return await loadLawImplementation(def, user);
      case "legalAwarenessCreation":
        return await loadLegalAwareness(def, user);
      case "legalAwarenessCreationZone":
        return await loadLegalAwarenessZone(def, user);
      case "legalTrainingProvision":
        return await loadLegalTraining(def, user);
      case "legalTrainingProvisionZone":
        return await loadLegalTrainingZone(def, user);
      default:
        if (def.state) return await loadConductLegalResearch(def, user, def.state.activeTab);
        throw new Error(`No loader for dashboard module: ${key}`);
    }
  } catch (e) {
    throw toError(e, key);
  }
}

// ---------------------------------------------------------------------------
// Legislative Drafting: pages/legislativeDrafting/legislativeDrafting.tsx
// ---------------------------------------------------------------------------

async function loadLegislativeDrafting(def: ModuleDef, user: any): Promise<ModuleCount> {
  // legislativeDrafting.tsx:221-223. The same service for every internal role.
  // The page swallows errors (toast plus an empty table, :230-233). We throw.
  const data = asArray(
    await (user?.user_type === "external" && user?.org_unit_id
      ? getLegislativeDraftingsByStakeHolderID(user.org_unit_id)
      : getLegislativeDraftings())
  );

  const S = LEGISLATIVE_DRAFTING_REQUEST_STATUS;
  // legislativeDrafting.tsx:290-328. Tab predicates; tabs at :28.
  const match = (req: any, tabKey: string): boolean => {
    if (tabKey === "All") return true;
    if (tabKey === "NewRequest")
      return (
        req.current_status === S.submitted.value ||
        (req.current_status === S.coordinator_remark_submitted.value &&
          req.forward_status === S.assigned_to_prosecutor.value) ||
        (req.current_status === S.director_remark_submitted.value &&
          req.forward_status === S.forwarded_to_coordinator.value) ||
        (req.current_status === S.head_remark_submitted.value &&
          req.forward_status === S.forwarded_to_director.value)
      );
    if (tabKey === "Published") return req.current_status === S.published.value;
    if (tabKey === "WaitingAdditionalInfo")
      return req.current_status === S.waiting_for_additional_info.value;
    return false; // the page leaves tabData undefined, giving []
  };

  const tabs = [
    tab("NewRequest", "New Request"),
    tab("Published", "Published"),
    tab("WaitingAdditionalInfo", "Waiting Additional Info"),
  ];
  const rows = data.filter((r) => match(r, "All")); // default tab "All" (:27)
  return {
    key: def.key,
    route: def.route,
    total: rows.length,
    byStatus: countTabs(data, tabs, match),
    // Row navigation is relative, navigate(`${row?.id}`) (:159).
    // The Name column shows organization_name / request_reference_number (:112-113).
    recent: rows.slice(0, RECENT_LIMIT).map((r) => ({
      id: String(r?.id),
      title: str(r?.organization_name) || str(r?.application_number) || String(r?.id),
      subtitle: str(r?.request_reference_number) || str(r?.application_number),
      status: str(r?.current_status) ?? null,
      date: str(r?.created_at) ?? null,
      route: `${def.route}/${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Legal Advisory / Sector Directives / Register Directives
//   pages/LegalAdvisory/legalAdvisory.tsx
//   pages/sectorDirectives/sectorDirectives.tsx
//   pages/registerDirectives/registerDirectives.tsx
// These three pages share one tab predicate; only the fetch chain differs and
// Register Directives adds a "Published" tab.
// ---------------------------------------------------------------------------

async function loadAdvisoryLike(
  def: ModuleDef,
  user: any,
  kind: "advisory" | "sector" | "register"
): Promise<ModuleCount> {
  const token = getToken(user);
  let raw: any;
  if (kind === "advisory") {
    // legalAdvisory.tsx:226-235
    if (user?.user_type === "external" && user?.org_unit_id) {
      raw = await getLegalAdvisoriesByStakeholder(user.org_unit_id);
    } else if (user?.roles?.some((r: string) => r === "LegalResearchProsecutor") && user?.id) {
      raw = await getLegalAdvisoriesByProsecutor(user.id);
    } else {
      raw = await getLegalAdvisories();
    }
  } else if (kind === "sector") {
    // sectorDirectives.tsx:229-236. There is no prosecutor branch.
    if (user?.user_type === "external" && user?.org_unit_id) {
      raw = await getSectorDirectivesByStakeholder(user.org_unit_id, token);
    } else {
      raw = await getSectorDirectives(token);
    }
  } else {
    // registerDirectives.tsx:228-238. The prosecutor branch uses user_id, not id.
    if (user?.user_type === "external" && user?.org_unit_id) {
      raw = await getRegisterDirectivesByStakeholder(token, user.org_unit_id);
    } else if (user?.roles?.includes("LegalResearchProsecutor") && user?.user_id) {
      raw = await getRegisterDirectivesByProsecutor(token, user.user_id);
    } else {
      raw = await getRegisterDirectives(token);
    }
  }
  // All three pages swallow errors (toast plus an empty table). We throw.
  const data = asArray(raw);

  // legalAdvisory.tsx:29 / sectorDirectives.tsx:30 / registerDirectives.tsx:31
  // Case-insensitive substring match on "registrar". Law Implementation and
  // Legal Translation use an exact "Registrar" match instead.
  const isRegistrar = !!user?.roles?.some((p: string) => p?.toLowerCase().includes("registrar"));
  const S = LEGISLATIVE_DRAFTING_REQUEST_STATUS;

  // legalAdvisory.tsx:310-340 / sectorDirectives.tsx:311-341 /
  // registerDirectives.tsx:313-345. Same predicate, keyed by tab id.
  const match = (row: any, activeTab: string): boolean => {
    if (isRegistrar) {
      const isArchived = String(row?.current_status || row?.status || "").toLowerCase() === "archived";
      const isForwardedToRegistrar = row?.forward_status === S.forwarded_to_registrar.value;
      if (!isForwardedToRegistrar && !isArchived) return false;
      const hasReference = !!(row?.registry_number || row?.letter_reference_number);
      if (activeTab === "Registered") return hasReference || isArchived;
      return !hasReference && !isArchived; // New Request
    }
    const status = row?.current_status;
    const isNew = status === S.submitted.value || row?.forward_status === S.not_forwarded?.value;
    const isAdditionalInfo = status === S.waiting_for_additional_info.value;
    const isAssignedToProsecutor = row?.forward_status === S.assigned_to_prosecutor.value;
    const isCompleted = String(status || "").toLowerCase() === "archived";
    const isPublished = row?.is_published === true; // register page only

    if (activeTab === "NewRequest") return isNew;
    if (activeTab === "AdditionalInfoRequested") return isAdditionalInfo;
    if (activeTab === "AssignedToProsecutor") return isAssignedToProsecutor;
    if (activeTab === "Completed") return isCompleted;
    if (kind === "register" && activeTab === "Published") return isPublished;
    if (activeTab === "InProgress") return !isNew && !isAdditionalInfo && !isCompleted;
    return true; // All
  };

  // Tabs: legalAdvisory.tsx:34-43 / sectorDirectives.tsx:36-45 / registerDirectives.tsx:36-46
  const tabs: TabDef[] = isRegistrar
    ? [tab("NewRequest", "New Request"), tab("Registered", "Registered")]
    : [
        tab("NewRequest", "New Request"),
        tab("InProgress", "In Progress"),
        tab("AdditionalInfoRequested", "Additional Info Requested"),
        tab("AssignedToProsecutor", "Assigned to Prosecutor"),
        tab("Completed", "Completed"),
        ...(kind === "register" ? [tab("Published", "Published")] : []),
      ];
  // Default tab: "New Request" for registrars, otherwise "All"
  // (legalAdvisory.tsx:31-33 and the matching lines in the other two pages).
  // For registrars, "New Request" IS the first tab, so it is both `total` and
  // the first byStatus entry.
  const defaultTab = isRegistrar ? "NewRequest" : "All";
  const rows = data.filter((r) => match(r, defaultTab));
  return {
    key: def.key,
    route: def.route,
    total: rows.length,
    byStatus: countTabs(data, tabs, match),
    // Rows navigate relatively with navigate(`${row?.id}`)
    // (legalAdvisory.tsx:168, sectorDirectives.tsx:171, registerDirectives.tsx:170).
    // The Name column shows organization_name / request_letter_reference_number.
    recent: rows.slice(0, RECENT_LIMIT).map((r) => ({
      id: String(r?.id),
      title: str(r?.organization_name) || str(r?.application_number) || String(r?.id),
      subtitle: str(r?.request_letter_reference_number) || str(r?.application_number),
      status: str(r?.current_status) ?? str(r?.status) ?? null,
      date: str(r?.created_at) ?? null,
      route: `${def.route}/${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Legal Translation: pages/legalTranslation/legalTranslation.tsx
// One component for /legal-translation, /legal-translation/external and
// /legal-translation/internal. The mode comes from the pathname (:43).
// ---------------------------------------------------------------------------

async function loadLegalTranslation(def: ModuleDef, user: any, isInternalMode: boolean): Promise<ModuleCount> {
  // legalTranslation.tsx:236-262. Quirk: the prosecutor branch checks
  // "LegalResearchProsecutor", so a LegalTranslationProsecutor gets ALL
  // external translations, not only the ones assigned to them.
  let raw: any;
  if (isInternalMode) {
    raw = await getLegalTranslationsInternal();
  } else if (user?.user_type === "external" && user?.org_unit_id) {
    raw = await getLegalTranslationsByStakeHolderID(user.org_unit_id);
  } else if (user?.roles?.some((role: string) => role === "LegalResearchProsecutor") && user?.id) {
    raw = await getLegalTranslationsByProsecutorID(user.id);
  } else {
    raw = await getLegalTranslationsExternal();
  }
  // The page swallows errors (toast plus an empty table, :264-267). We throw.
  const data = asArray(raw);

  // legalTranslation.tsx:35. Exact "Registrar" match.
  const isRegistrar = !!user?.roles?.some((r: string) => r === "Registrar");

  // legalTranslation.tsx:346-378
  const match = (row: any, activeTab: string): boolean => {
    const status = row?.current_status as string;
    const fwd = row?.forward_status as string;
    if (isRegistrar) {
      const isForwardedToRegistrar = fwd === "sent_to_registrar";
      const hasReference = !!(row?.request_letter_reference_number || row?.registry_number);
      if (!isForwardedToRegistrar && !hasReference) return false;
      if (activeTab === "Registered") return hasReference;
      return !hasReference; // New Request
    }
    const isNew = fwd === "not_forwarded" || fwd === "forwarded_to_head";
    const isAdditionalInfo =
      status === "Waiting for Additional Information" || status === "additional_info_requested";
    const isAssignedToProsecutor = fwd === "assigned_to_prosecutor";
    const isCompleted = status === "Archived";
    const isPublished = status === "Published" || row?.is_published === true;

    if (activeTab === "NewRequest") return isNew;
    if (activeTab === "AdditionalInfoRequested") return isAdditionalInfo;
    if (activeTab === "AssignedToProsecutor") return isAssignedToProsecutor;
    if (activeTab === "Completed") return isCompleted;
    if (activeTab === "Published") return isPublished;
    if (activeTab === "InProgress") return !isNew && !isAdditionalInfo && !isCompleted && !isPublished;
    return true; // All
  };

  // Tabs: legalTranslation.tsx:45-55. Default tab is at :36.
  const tabs: TabDef[] = isRegistrar
    ? [tab("NewRequest", "New Request"), tab("Registered", "Registered")]
    : [
        tab("NewRequest", "New Request"),
        tab("InProgress", "In Progress"),
        tab("AdditionalInfoRequested", "Additional Info Requested"),
        tab("AssignedToProsecutor", "Assigned to Prosecutor"),
        tab("Completed", "Completed"),
        tab("Published", "Published"),
      ];
  const rows = data.filter((r) => match(r, isRegistrar ? "NewRequest" : "All"));
  return {
    key: def.key,
    route: def.route,
    total: rows.length,
    byStatus: countTabs(data, tabs, match),
    // legalTranslation.tsx:178. /legal-translation/internal/:id in internal
    // mode, otherwise /legal-translation/:id (also from /external).
    recent: rows.slice(0, RECENT_LIMIT).map((r) => ({
      id: String(r?.id),
      title: str(r?.organization_name) || str(r?.application_number) || String(r?.id),
      subtitle: str(r?.request_letter_reference_number) || str(r?.application_number),
      status: str(r?.current_status) ?? str(r?.status) ?? null,
      date: str(r?.created_at) ?? null,
      route: `/${isInternalMode ? "legal-translation/internal/" : "legal-translation/"}${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Law Implementation: pages/lawImplementation/lawImplementation.tsx
// ---------------------------------------------------------------------------

async function loadLawImplementation(def: ModuleDef, user: any): Promise<ModuleCount> {
  // lawImplementation.tsx:196-241. Every non-prosecutor uses the registry
  // number filter, with registry_number = user.admin_unit.
  const isProsecutor = !!user?.roles?.some((role: string) => role === "LegalResearchProsecutor");
  const registryNumber = user?.admin_unit || "";
  const userId = user?.user_id || "";
  // lawImplementation.tsx:175. The page always loads the audit (sector) list too.
  // The page swallows both errors (toast plus an empty table). We throw.
  const [rawData, rawTargets] = await Promise.all([
    isProsecutor
      ? getLawImplementationsByRegistryAndUser(registryNumber, userId)
      : getLawImplementationsByRegistryNumber(registryNumber),
    getAuditLawImplementations(),
  ]);
  const data = asArray(rawData);
  // lawImplementation.tsx:310-318. Each audit row takes its parent's
  // application_number.
  const targets = asArray(rawTargets).map((research: any) => {
    const m = data.find(
      (d: any) => String(d.id) === String(research.followup?.id || research.followup || research.followup_id)
    );
    return { ...research, application_number: research.application_number || m?.application_number };
  });

  // lawImplementation.tsx:33. Exact "Registrar" match.
  const isRegistrar = !!user?.roles?.some((r: string) => r === "Registrar");

  // lawImplementation.tsx:329-358
  const rowsFor = (activeTab: string): any[] => {
    if (isRegistrar) {
      return targets.filter((req: any) => {
        const hasReference = !!(
          req?.adminn_unit_id || // sic: the page uses "adminn_unit_id"
          req?.letter_reference_number ||
          req?.current_status === LAW_IMPLEMENTATION_REQUEST_STATUS.letter_refnum_submitted.value
        );
        if (activeTab === "Registered") return hasReference;
        return !hasReference; // New Request
      });
    }
    if (activeTab === "NewRequest")
      return data.filter((req: any) => req.status === LAW_IMPLEMENTATION_REQUEST_STATUS.pending.value);
    if (activeTab === "SectorRequest") return targets;
    if (activeTab === "ZonalRequest") return targets.filter((req: any) => req.is_zonal_audit === true);
    return data; // All
  };

  // Tabs: lawImplementation.tsx:35-37. Default tab is at :34.
  const tabs: TabDef[] = isRegistrar
    ? [tab("NewRequest", "New Request"), tab("Registered", "Registered")]
    : [tab("NewRequest", "New Request"), tab("SectorRequest", "Sector Request"), tab("ZonalRequest", "Zonal Request")];
  const rows = rowsFor(isRegistrar ? "NewRequest" : "All");
  return {
    key: def.key,
    route: def.route,
    total: rows.length,
    byStatus: tabs.map((t) => ({ key: t.key, labelKey: t.labelKey, label: t.label, count: rowsFor(t.key).length })),
    // lawImplementation.tsx:112-116. Registrars go to audit/:id. So do the
    // "Sector Request" / "Zonal Request" tabs, but only when the tab text
    // equals the English literal. The default "All" tab goes to :id.
    recent: rows.slice(0, RECENT_LIMIT).map((r: any) => ({
      id: String(r?.id),
      title: str(r?.application_number) || str(r?.registry_number) || String(r?.id),
      subtitle: undefined,
      status: str(r?.current_status) ?? str(r?.current_approval_status) ?? null,
      date: str(r?.created_at) ?? null,
      route: isRegistrar ? `${def.route}/audit/${r?.id}` : `${def.route}/${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Legal Awareness Creation: pages/legalAwarenessCreation/legalAwarenessCreation.tsx
// ---------------------------------------------------------------------------

async function loadLegalAwareness(def: ModuleDef, user: any): Promise<ModuleCount> {
  const roles = user?.roles;
  // legalAwarenessCreation.tsx:209-219
  let raw: any;
  if (roles?.includes("WoredaLDR")) {
    raw = await getAllDistributedTowerData();
  } else if (roles?.includes("ZonalLRD") || roles?.includes("ZonalLIF")) {
    // Zonal users' sidebar links to /legal-awareness-creation-zone, so this
    // branch only runs if they open this route directly. Kept to mirror the page.
    raw = await getLegalAwarenessZoneByAdminUnit(user.admin_unit || "");
  } else if (roles?.some((p: string) => p === "LegalResearchProsecutor") && user?.id) {
    raw = await getLegalAwarenessByProsecutor(user.id);
  } else {
    raw = await getLegalAwareness();
  }
  // The page swallows errors (toast plus an empty table, :224-226). We throw.
  const data = asArray(raw);
  const isWoreda = !!roles?.includes("WoredaLDR"); // :157

  // legalAwarenessCreation.tsx:276-291
  const match = (row: any, activeTab: string): boolean => {
    const status = row?.current_approval_status || row?.current_status;
    const fwd = row?.forward_status;
    const isScheduled = status === "session_scheduled";
    const isCompleted =
      String(status || "").toLowerCase() === "archived" || status === "Published" || row?.is_published === true;
    const isNew = fwd === "not_forwarded" || !fwd;
    if (activeTab === "NewRequest") return isNew;
    if (activeTab === "Scheduled") return isScheduled;
    if (activeTab === "Completed") return isCompleted;
    if (activeTab === "InProgress") return !isNew && !isCompleted && !isScheduled;
    return true; // All
  };

  // Woreda users get no TabNav (:300) and see every row unfiltered (:320).
  const rows = isWoreda ? data : data.filter((r) => match(r, "All"));
  const tabs: TabDef[] = isWoreda
    ? []
    : [
        tab("NewRequest", "New Request"),
        tab("InProgress", "In Progress"),
        tab("Scheduled", "Scheduled"),
        tab("Completed", "Completed"),
      ]; // :26-32
  return {
    key: def.key,
    route: def.route,
    total: rows.length,
    byStatus: countTabs(data, tabs, match),
    // :131-136 / :193. Woreda users go to woreda/:id. The page also passes
    // { state: { woreda: row } }, which a route string can't carry, and
    // SingleWoredaAwareness may depend on it.
    recent: rows.slice(0, RECENT_LIMIT).map((r: any) =>
      isWoreda
        ? {
            id: String(r?.id),
            title: str(r?.admin_unit_name) || String(r?.id),
            subtitle: undefined,
            status: r?.report_summary ? "Submitted" : "NotSubmitted",
            date: str(r?.sent_date) ?? null,
            route: `${def.route}/woreda/${r?.id}`,
          }
        : {
            id: String(r?.id),
            title: str(r?.target_audience) || str(r?.application_number) || String(r?.id),
            subtitle: str(r?.application_number),
            status: str(r?.current_approval_status) ?? str(r?.current_status) ?? null,
            date: str(r?.created_at) ?? null,
            route: `${def.route}/${r?.id}`,
          }
    ),
  };
}

// ---------------------------------------------------------------------------
// Legal Awareness Creation (zone): pages/legalAwarenessCreation/legalAwarenessCreationZone.tsx
// ---------------------------------------------------------------------------

async function loadLegalAwarenessZone(def: ModuleDef, user: any): Promise<ModuleCount> {
  // legalAwarenessCreationZone.tsx:82. Same call for every role.
  // The page swallows errors (toast plus an empty table, :89-92). We throw.
  const data = asArray(await getLegalAwarenessZoneByAdminUnit(user?.admin_unit || ""));
  // legalAwarenessCreationZone.tsx:139-158. Quirk: the awareness page uses the
  // TRAINING status constants.
  const match = (req: any, activeTab: string): boolean => {
    if (activeTab === "NewRequest") return req.status === LEGAL_TRAINING_PROVISION_REQUEST_STATUS.submitted.value;
    if (activeTab === "Internal")
      return req.status === LEGAL_TRAINING_PROVISION_REQUEST_STATUS.submitted_internal.value;
    return true;
  };
  const tabs = [tab("Internal", "Internal"), tab("NewRequest", "New Request")]; // :23 order
  return {
    key: def.key,
    route: def.route,
    total: data.length, // default "All" (:22)
    byStatus: countTabs(data, tabs, match),
    // :63 navigate(`${row?.id}`)
    recent: data.slice(0, RECENT_LIMIT).map((r: any) => ({
      id: String(r?.id),
      title: str(r?.admin_unit_name) || String(r?.id),
      subtitle: undefined,
      status: r?.report_summary ? "Submitted" : "NotSubmitted",
      date: str(r?.sent_date) ?? null,
      route: `${def.route}/${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Legal Training Provision: pages/legalTraningProvision/legalTraningProvision.tsx
// ---------------------------------------------------------------------------

async function loadLegalTraining(def: ModuleDef, user: any): Promise<ModuleCount> {
  // legalTraningProvision.tsx:285-291
  let raw: any;
  if (user?.user_type === "external" && user?.org_unit_id) {
    raw = await getLegalTrainingProvisionsByStakeHolder(user.org_unit_id);
  } else if (user?.roles?.some((p: string) => p === "LegalResearchProsecutor") && user?.id) {
    raw = await getLegalTrainingProvisionsByProsecutor(user.id);
  } else {
    raw = await getLegalTrainingProvisions();
  }
  // The page swallows errors (toast plus an empty table, :296-298). We throw.
  const data = asArray(raw);
  const S = LEGAL_TRAINING_PROVISION_REQUEST_STATUS;

  // legalTraningProvision.tsx:374-407
  const match = (req: any, activeTab: string): boolean => {
    if (activeTab === "NewRequest") return req.status === S.submitted.value;
    if (activeTab === "Internal") return req.requested_by === "internal";
    if (activeTab === "External") return req.requested_by === "stakeholder";
    if (activeTab === "TeamAssigned") return req.status === S.team_assigned.value;
    if (activeTab === "TrainingScheduled") return req.status === S.training_scheduled.value;
    return true;
  };
  // Tabs are shown only to internal users (:35-44, :413). "TeamAssigned" and
  // "TrainingScheduled" are not in any locale, so the page shows the raw key.
  const tabs: TabDef[] =
    user?.user_type === "internal"
      ? [
          tab("NewRequest", "New Request"),
          tab("Internal", "Internal"),
          tab("External", "External"),
          tab("TeamAssigned", "Team Assigned"),
          tab("TrainingScheduled", "Training Scheduled"),
        ]
      : [];
  return {
    key: def.key,
    route: def.route,
    total: data.length, // default "All" (:34)
    byStatus: countTabs(data, tabs, match),
    // :214 navigate(`${row?.id}`). The Name column shows organization_name /
    // request_letter_ref_no (:128-129).
    recent: data.slice(0, RECENT_LIMIT).map((r: any) => ({
      id: String(r?.id),
      title: str(r?.organization_name) || str(r?.application_number) || String(r?.id),
      subtitle: str(r?.request_letter_ref_no) || str(r?.application_number),
      status: str(r?.status) ?? str(r?.current_status) ?? null,
      date: str(r?.created_at) ?? null,
      route: `${def.route}/${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Legal Training Provision (zone): pages/legalTraningProvision/LegalTraningProvisionZone.tsx
// ---------------------------------------------------------------------------

async function loadLegalTrainingZone(def: ModuleDef, user: any): Promise<ModuleCount> {
  // LegalTraningProvisionZone.tsx:101. On error the page only logs to the
  // console (:110-113, no toast) and shows an empty table. We throw.
  const data = asArray(await getLegalTrainingProvisionsZoneByAdminUnit(user?.admin_unit || ""));
  const S = LEGAL_TRAINING_PROVISION_REQUEST_STATUS;
  // LegalTraningProvisionZone.tsx:168-187
  const match = (req: any, activeTab: string): boolean => {
    if (activeTab === "NewRequest") return req.status === S.submitted.value;
    if (activeTab === "Internal") return req.status === S.submitted_internal.value;
    return true;
  };
  const tabs = [tab("Internal", "Internal"), tab("NewRequest", "New Request")]; // :22 order
  return {
    key: def.key,
    route: def.route,
    total: data.length, // default "All" (:21)
    byStatus: countTabs(data, tabs, match),
    // :81 navigate(`${row?.id}`). The Name column shows admin_unit_name /
    // request_letter_ref_no (:59-60).
    recent: data.slice(0, RECENT_LIMIT).map((r: any) => ({
      id: String(r?.id),
      title: str(r?.admin_unit_name) || String(r?.id),
      subtitle: str(r?.request_letter_ref_no),
      status: str(r?.status) ?? str(r?.current_status) ?? null,
      date: str(r?.sent_date) ?? str(r?.created_at) ?? null,
      route: `${def.route}/${r?.id}`,
    })),
  };
}

// ---------------------------------------------------------------------------
// Conduct Legal Research: pages/conductLegalResearch/condactLegalResearch.tsx
//
// One page for every sidebar sub-item. The sidebar passes the ENGLISH tab
// label as state.activeTab (Sidebar.tsx:172-174). The page compares it with
// t("ExternalRequest") etc. (condactLegalResearch.tsx:664-759). In am/or the
// strings don't match, so the table is empty and no sub-buttons appear. We
// copy that by comparing against i18n.t() for the current language.
// "Archived" and "Proposal Submission" have no branch in the page, so they
// always show 0 rows.
// ---------------------------------------------------------------------------

interface ClrData {
  main: any[]; // after the role filter at :494-554
  internal: any; // :437. Raw response; DataTable ignores non-arrays.
  prosecutorInternal: any[]; // :454-468
  committee: any; // :417. Raw response.
}

// condactLegalResearch.tsx:494-553. Role filter on the main list; the first
// matching role wins. Roles that match nothing see the full list.
function clrRoleFilter(data: any[], user: any): any[] {
  const has = (r: string) => !!user?.roles?.some((p: string) => p === r);
  const S = LEGAL_RESEARCH_REQUEST_STATUS;
  if (has("GeneralAttorney") || has("LRDIFUDeputy")) return data;
  if (has("OAGBManagementHead"))
    return data?.filter((req: any) => req.forward_status === S.forwarded_to_management.value);
  if (has("LRDIFUDDirector")) return data;
  if (has("LegalResearchCoordinator"))
    return data?.filter(
      (req: any) =>
        (req.requested_by === "external" && req.forward_status === S.forwarded_to_director.value) ||
        (req.forward_status !== S.not_forwarded.value &&
          req.forward_status !== S.forwarded_to_head.value &&
          req.forward_status !== S.forwarded_to_management.value &&
          req.forward_status !== S.forwarded_to_director.value) ||
        (req.requested_by === "internal" && req.requested_by_id === user?.user_id) ||
        req.status === S.proposal_approved.value ||
        (req.status === S.title_accepted.value && req.forward_status === S.forwarded_to_head.value)
    );
  if (has("LegalResearchProsecutor")) return data;
  if (has("Registrar")) return data?.filter((req: any) => req.forward_status === S.sent_to_registrar.value);
  return data;
}

// condactLegalResearch.tsx:661-765. tabData for an activeTab string.
function clrTabData(activeTab: string, d: ClrData, user: any): any {
  const S = LEGAL_RESEARCH_REQUEST_STATUS;
  const filteredData = d.main;
  const isExternalish = (req: any) => req.requested_by === "external" || req.requested_by === "";
  const isScheduled = (req: any) =>
    ((req.status === S.proposal_approved.value || req.status === S.proposal_update.value) &&
      req.schedules?.find((s: any) => s.purpose === "proposal_presentation")) ||
    (req.status === S.title_approved.value && req.schedules?.find((s: any) => s.purpose === "title_presentation"));
  // Mirrors `committeeData?.filter(...)`. A non-array response would throw
  // inside the page's effect; here it counts as [].
  const committee = asArray(d.committee);

  if (activeTab === tr("NewRequest")) {
    return filteredData.filter((req: any) => req.status === S.pending.value && req.requested_by === "internal");
  } else if (activeTab === tr("InternalRequest")) {
    return filteredData.filter((req: any) => req.requested_by === "internal");
  } else if (activeTab === tr("InternalRequestTopics")) {
    const isProsecutor = !!user?.roles?.some((p: string) => p === "LegalResearchProsecutor");
    return isProsecutor && d.prosecutorInternal.length > 0 ? d.prosecutorInternal : d.internal;
  } else if (activeTab === tr("ScheduledPresentation")) {
    return filteredData.filter((req: any) => req.requested_by === "internal" && isScheduled(req));
  } else if (activeTab === tr("InternalPublished")) {
    return filteredData.filter((req: any) => req.requested_by === "internal" && req.is_published);
  } else if (activeTab === tr("ExternalRequest") || activeTab === tr("LegalResearchRequest")) {
    return filteredData.filter(isExternalish);
  } else if (activeTab === tr("ExternalNewRequest")) {
    return filteredData.filter((req: any) => req.status === S.pending.value && isExternalish(req));
  } else if (activeTab === tr("ExternalScheduledPresentation")) {
    return filteredData.filter((req: any) => isExternalish(req) && isScheduled(req));
  } else if (activeTab === tr("ExternalPublished")) {
    return filteredData.filter((req: any) => isExternalish(req) && req.is_published);
  } else if (activeTab === tr("CommitteeRequest")) {
    return d.committee;
  } else if (activeTab === tr("CommitteeNewRequest")) {
    return committee.filter((req: any) => req.status === S.pending.value);
  } else if (activeTab === tr("CommitteeScheduledPresentation")) {
    return committee.filter(isScheduled);
  } else if (activeTab === tr("CommitteePublished")) {
    return committee.filter((req: any) => req.is_published);
  }
  return undefined;
}

type ClrGroup = "internal" | "external" | "committee" | null;

// condactLegalResearch.tsx:768-788
function clrGroup(activeTab: string): ClrGroup {
  if (["InternalRequest", "InternalRequestTopics", "NewRequest", "ScheduledPresentation", "InternalPublished"].some((k) => activeTab === tr(k)))
    return "internal";
  if (["ExternalRequest", "LegalResearchRequest", "ExternalNewRequest", "ExternalScheduledPresentation", "ExternalPublished"].some((k) => activeTab === tr(k)))
    return "external";
  if (["CommitteeRequest", "CommitteeNewRequest", "CommitteeScheduledPresentation", "CommitteePublished"].some((k) => activeTab === tr(k)))
    return "committee";
  return null;
}

// Sub-buttons each group shows (condactLegalResearch.tsx:851-1005), minus
// the "...List" button, which is the group's whole list (our `total`). The
// internal group's own button ("Internal Request") is its whole list; its
// first sub-button, "Internal Request Topics", is a separate dataset.
function clrSubTabs(group: ClrGroup, user: any): TabDef[] {
  if (group === "internal" && user?.user_type !== "external") {
    return [
      tab("InternalRequestTopics", "Internal Request Topics"),
      tab("NewRequest", "New Request"),
      tab("ScheduledPresentation", "Scheduled Presentation"),
      tab("InternalPublished", "Published Research", "PublishedResearch"),
    ];
  }
  if (group === "external") {
    return [
      tab("ExternalNewRequest", "New Request", "NewRequest"),
      tab("ExternalScheduledPresentation", "Scheduled Presentation", "ScheduledPresentation"),
      tab("ExternalPublished", "Published Research", "PublishedResearch"),
    ];
  }
  if (group === "committee") {
    return [
      tab("CommitteeNewRequest", "New Request", "NewRequest"),
      tab("CommitteeScheduledPresentation", "Scheduled Presentation", "ScheduledPresentation"),
      tab("CommitteePublished", "Published Research", "PublishedResearch"),
    ];
  }
  return [];
}

async function loadConductLegalResearch(def: ModuleDef, user: any, activeTab: string): Promise<ModuleCount> {
  const token = getToken(user);
  const group = clrGroup(activeTab);
  const subTabs = clrSubTabs(group, user);
  // Each sub-button calls handleTabChange(t(key)), so its activeTab is the
  // translated string.
  const tabStrings = [activeTab, ...subTabs.map((s) => tr(s.key))];
  const needs = (k: string) => tabStrings.some((s) => s === tr(k));
  const needCommittee = group === "committee";
  const needTopics = needs("InternalRequestTopics");
  const needMain = tabStrings.some((s) => {
    const g = clrGroup(s);
    return (g === "internal" && s !== tr("InternalRequestTopics")) || g === "external";
  });

  const d: ClrData = { main: [], internal: {}, prosecutorInternal: [], committee: {} };

  const jobs: Promise<void>[] = [];
  if (needMain) {
    // condactLegalResearch.tsx:472-492. With no bearer the page returns early
    // and the list stays empty.
    jobs.push(
      (async () => {
        if (!token) return;
        const decoded = getTokenPayloadSafely(token);
        let data: any;
        if (user?.user_type === "external" && decoded?.organization?.uu_id) {
          data = await getLegalResearchRequestsByStakeHolder(token, decoded.organization.uu_id);
        } else {
          data = await getLegalResearchRequests(token);
        }
        // The page swallows errors (toast plus an empty table, :555-557). We throw.
        // A non-array response would make the page's `data?.filter` throw
        // (caught: toast plus an empty table). Here it counts as [].
        d.main = asArray(clrRoleFilter(asArray(data), user));
      })()
    );
  }
  if (needCommittee && token) {
    // condactLegalResearch.tsx:402-425. Runs only when contextData.token is
    // set. The page swallows errors (toast; committeeData stays {}). We throw.
    jobs.push(
      (async () => {
        d.committee =
          user?.user_type === "external" && user?.org_unit_id
            ? await getCommitteeLegalResearchRequestsByStakeholder(token, user.org_unit_id)
            : await getCommitteeLegalResearchRequests(token);
      })()
    );
  }
  if (needTopics && token) {
    // condactLegalResearch.tsx:433-444. The page swallows errors. We throw.
    jobs.push(
      (async () => {
        d.internal = await getInternalLegalResearchRequests(token);
      })()
    );
    // condactLegalResearch.tsx:447-469. The page's own fallback on failure is
    // [] and then the general internal list, so that is copied (no throw).
    const isProsecutor = !!user?.roles?.some((p: string) => p === "LegalResearchProsecutor");
    if (isProsecutor && user?.user_id) {
      jobs.push(
        getInternalLegalResearchRequestsByProsecutor(token, user.user_id)
          .then((data: any) => {
            d.prosecutorInternal = data;
          })
          .catch(() => {
            d.prosecutorInternal = [];
          })
      );
    }
  }
  await Promise.all(jobs);
  // `prosecutorInternalData.data.length` (:676). Keep a non-array response
  // from crashing.
  if (!Array.isArray(d.prosecutorInternal)) d.prosecutorInternal = asArray(d.prosecutorInternal);

  // DataTable shows [] for non-array data (DataTable.tsx:62), and the page
  // does `tabData || []` (:763).
  const rows = asArray(clrTabData(activeTab, d, user));

  // handleViewDetails (:83-94). The detail path is relative to
  // /conduct-legal-research. Only the exact "Committee Request" and "Internal
  // Request Topics" tabs get the committee/ and internal/ prefixes.
  const detailPath = (row: any) =>
    activeTab === tr("InternalRequestTopics")
      ? `internal/${row?.id}`
      : activeTab === tr("CommitteeRequest")
      ? `committee/${row?.id}`
      : `${row?.id}`;

  return {
    key: def.key,
    route: def.route,
    state: def.state,
    total: rows.length,
    byStatus: subTabs.map((s) => ({
      key: s.key,
      labelKey: s.labelKey,
      label: s.label,
      count: asArray(clrTabData(tr(s.key), d, user)).length,
    })),
    // The Name column (:176-185) shows research_task_id for internal rows,
    // otherwise organization_name / letter_reference_number.
    recent: rows.slice(0, RECENT_LIMIT).map((r: any) => ({
      id: String(r?.id),
      title:
        (r?.requested_by === "internal" ? str(r?.research_task_id) : str(r?.organization_name)) ||
        str(r?.research_task) ||
        str(r?.application_number) ||
        String(r?.id),
      subtitle:
        r?.requested_by === "internal"
          ? str(r?.application_number)
          : str(r?.letter_reference_number) || str(r?.application_number),
      status: str(r?.status) ?? null,
      date: str(r?.created_at) ?? null,
      route: `${def.route}/${detailPath(r)}`,
    })),
  };
}
